import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowLeftIcon,
  EyeIcon,
  DocumentMagnifyingGlassIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

interface CorporateInfo {
  company_name: string;
  company_registration_number: string;
  company_address: string;
  company_registration_doc_url?: string;
  company_logo_url?: string;
}

interface CorporateUserRaw {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  approval_status: string;
  rejection_reason?: string;
  customer_type: string;
  corporate_info?: CorporateInfo;
  created_at: string;
}

interface CorporateUser {
  id: string;
  uuid: string;
  numeric_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  approval_status: string;
  rejection_reason?: string;
  customer_type: string;
  company_name: string;
  company_registration_number: string;
  company_address: string;
  company_registration_doc_url?: string;
  company_logo_url?: string;
  created_at: string;
}

const mapCorporateUser = (raw: CorporateUserRaw): CorporateUser => ({
  id: String(raw.id),
  uuid: raw.uuid,
  numeric_id: raw.id,
  first_name: raw.first_name,
  last_name: raw.last_name,
  email: raw.email,
  phone: raw.phone,
  approval_status: raw.approval_status,
  rejection_reason: raw.rejection_reason,
  customer_type: raw.customer_type,
  company_name: raw.corporate_info?.company_name || '',
  company_registration_number: raw.corporate_info?.company_registration_number || '',
  company_address: raw.corporate_info?.company_address || '',
  company_registration_doc_url: raw.corporate_info?.company_registration_doc_url,
  company_logo_url: raw.corporate_info?.company_logo_url,
  created_at: raw.created_at,
});

const CorporateApprovals: React.FC = () => {
  const navigate = useNavigate();
  const [pendingCorporate, setPendingCorporate] = useState<CorporateUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<CorporateUser | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [viewingDoc, setViewingDoc] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingCorporate();
  }, []);

  const fetchPendingCorporate = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/admin/pending-corporate-approvals', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const mappedUsers = (data.pending_corporate || []).map((user: CorporateUserRaw) => mapCorporateUser(user));
        setPendingCorporate(mappedUsers);
      }
    } catch (error) {
      console.error('Error fetching pending corporate approvals:', error);
      showNotification('error', 'Failed to fetch pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleApprove = async (user: CorporateUser) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/admin/approve-user/${user.uuid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showNotification('success', `${user.company_name} has been approved successfully`);
        setSelectedUser(null);
        fetchPendingCorporate();
      } else {
        const data = await response.json();
        showNotification('error', data.detail || 'Failed to approve');
      }
    } catch (error) {
      showNotification('error', 'Failed to approve corporate account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/admin/reject-user/${selectedUser.uuid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectionReason || 'No reason provided' })
      });

      if (response.ok) {
        showNotification('success', `${selectedUser.company_name} has been rejected`);
        setShowRejectModal(false);
        setSelectedUser(null);
        setRejectionReason('');
        fetchPendingCorporate();
      } else {
        const data = await response.json();
        showNotification('error', data.detail || 'Failed to reject');
      }
    } catch (error) {
      showNotification('error', 'Failed to reject corporate account');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading corporate approvals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 font-poppins"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 rounded-lg p-3">
              <BuildingOffice2Icon className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 font-poppins">Corporate Approvals</h1>
              <p className="mt-1 text-gray-600 font-poppins">Review and approve corporate customer registrations</p>
            </div>
          </div>
        </div>

        {notification && (
          <div className={`mb-6 p-4 rounded-lg ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5 mr-2" />
              ) : (
                <XCircleIcon className="h-5 w-5 mr-2" />
              )}
              <span className="font-poppins">{notification.message}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Pending Approvals</p>
                <p className="mt-2 text-3xl font-bold text-orange-600 font-poppins">{pendingCorporate.length}</p>
              </div>
              <div className="bg-orange-100 rounded-lg p-3">
                <ClockIcon className="h-8 w-8 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {pendingCorporate.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <BuildingOffice2Icon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 font-poppins mb-2">No Pending Corporate Approvals</h3>
            <p className="text-gray-600 font-poppins">All corporate registrations have been reviewed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingCorporate.map((user) => (
              <div key={user.uuid} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex items-start gap-4">
                    {user.company_logo_url ? (
                      <img
                        src={user.company_logo_url}
                        alt={user.company_name}
                        className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-orange-100 flex items-center justify-center">
                        <BuildingOffice2Icon className="h-8 w-8 text-orange-600" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 font-poppins">{user.company_name}</h3>
                      <p className="text-sm text-gray-500 font-poppins">CAC: {user.company_registration_number}</p>
                      <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <EnvelopeIcon className="h-4 w-4" />
                          {user.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <PhoneIcon className="h-4 w-4" />
                          {user.phone}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1 font-poppins">
                        Contact: {user.first_name} {user.last_name}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1 font-poppins"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Details
                    </button>
                    <button
                      onClick={() => handleApprove(user)}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1 font-poppins disabled:opacity-50"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowRejectModal(true);
                      }}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1 font-poppins disabled:opacity-50"
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Reject
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600 font-poppins">
                    <span className="font-semibold">Address:</span> {user.company_address}
                  </p>
                  <p className="text-xs text-gray-400 mt-2 font-poppins">
                    Applied: {new Date(user.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedUser && !showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedUser(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 font-poppins">Corporate Registration Details</h3>
                  <button onClick={() => setSelectedUser(null)} className="text-gray-400 hover:text-gray-500">
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="flex items-center gap-4 mb-6">
                  {selectedUser.company_logo_url ? (
                    <img
                      src={selectedUser.company_logo_url}
                      alt={selectedUser.company_name}
                      className="w-20 h-20 rounded-xl object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-orange-100 flex items-center justify-center">
                      <BuildingOffice2Icon className="h-10 w-10 text-orange-600" />
                    </div>
                  )}
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 font-poppins">{selectedUser.company_name}</h4>
                    <p className="text-gray-500 font-poppins">CAC: {selectedUser.company_registration_number}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 font-poppins mb-1">Contact Person</p>
                    <p className="font-semibold text-gray-900 font-poppins">{selectedUser.first_name} {selectedUser.last_name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 font-poppins mb-1">Email</p>
                    <p className="font-semibold text-gray-900 font-poppins text-sm">{selectedUser.email}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 font-poppins mb-1">Phone</p>
                    <p className="font-semibold text-gray-900 font-poppins">{selectedUser.phone}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 font-poppins mb-1">Applied On</p>
                    <p className="font-semibold text-gray-900 font-poppins">{new Date(selectedUser.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-xs text-gray-500 font-poppins mb-1">Business Address</p>
                  <p className="font-semibold text-gray-900 font-poppins">{selectedUser.company_address}</p>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-bold text-gray-700 font-poppins mb-3">Uploaded Documents</h5>
                  <div className="flex gap-3">
                    {selectedUser.company_registration_doc_url ? (
                      <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                        <DocumentTextIcon className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-green-800 font-poppins">CAC Certificate</span>
                        <button
                          onClick={() => setViewingDoc(selectedUser.company_registration_doc_url!)}
                          className="p-1 hover:bg-green-100 rounded"
                          title="View Document"
                        >
                          <DocumentMagnifyingGlassIcon className="h-4 w-4 text-green-600" />
                        </button>
                        <a
                          href={selectedUser.company_registration_doc_url}
                          download
                          className="p-1 hover:bg-green-100 rounded"
                          title="Download"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4 text-green-600" />
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-500 font-poppins">No CAC document</span>
                      </div>
                    )}

                    {selectedUser.company_logo_url && (
                      <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                        <span className="text-sm text-blue-800 font-poppins">Company Logo</span>
                        <button
                          onClick={() => setViewingDoc(selectedUser.company_logo_url!)}
                          className="p-1 hover:bg-blue-100 rounded"
                          title="View Logo"
                        >
                          <EyeIcon className="h-4 w-4 text-blue-600" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(true);
                  }}
                  disabled={actionLoading}
                  className="px-6 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors font-poppins disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(selectedUser)}
                  disabled={actionLoading}
                  className="px-6 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors font-poppins disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
            }}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-red-100 rounded-full p-2">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 font-poppins">Reject Application</h3>
                </div>

                <p className="text-gray-600 font-poppins mb-4">
                  You are about to reject the corporate registration for <strong>{selectedUser.company_name}</strong>. 
                  Please provide a reason so they can address the issues and resubmit.
                </p>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 font-poppins mb-2">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                    placeholder="Please specify why this application is being rejected..."
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 font-poppins focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionReason('');
                  }}
                  className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-poppins"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading || !rejectionReason.trim()}
                  className="px-6 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors font-poppins disabled:opacity-50"
                >
                  {actionLoading ? 'Rejecting...' : 'Reject Application'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewingDoc && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-90">
          <div className="flex items-center justify-center min-h-screen p-4">
            <button
              onClick={() => setViewingDoc(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
            >
              <XCircleIcon className="h-8 w-8" />
            </button>
            <div className="max-w-4xl w-full">
              {viewingDoc.match(/\.(jpg|jpeg|png|gif|svg)$/i) ? (
                <img src={viewingDoc} alt="Document" className="max-w-full max-h-[80vh] mx-auto rounded-lg" />
              ) : viewingDoc.match(/\.pdf$/i) ? (
                <iframe src={viewingDoc} className="w-full h-[80vh] rounded-lg bg-white" title="Document Viewer" />
              ) : (
                <div className="bg-white rounded-lg p-8 text-center">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-poppins">Unable to preview this file type</p>
                  <a href={viewingDoc} download className="mt-4 inline-block px-6 py-2 bg-orange-500 text-white rounded-lg font-poppins">
                    Download File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateApprovals;
